import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    TextField,
    Typography,
} from "@mui/material";

import { StyledTextField } from "../../../Components/EditVehicleDetails/EditDialogShared";
import SingleDateTimePicker from "../../../Components/SingleDateTimePicker";
import CreateEditEntityDialog from "../Network/CreateEditEntityDialog";
import { getAssetSearchSuggestions } from "../../../Api/AssetApi";
import {
    BandHead,
    RouteCaption,
    FieldLabel,
    FORM_TOKENS,
    FIELD_METRICS,
    FormBand,
    Helper,
    SELECT_ITEM_SX,
    SelectItemText,
    WORK_ORDER_AUTOCOMPLETE_INPUT_SX,
    WORK_ORDER_AUTOCOMPLETE_SX,
    WORK_ORDER_AUTOCOMPLETE_SLOT_PROPS,
    WORK_ORDER_CLEARABLE_AUTOCOMPLETE_SX,
    WORK_ORDER_DATE_FIELD_SX,
    WorkOrderSelectField,
} from "./WorkOrderFormShared";
import {
    ENTRY_MODE,
    MAX_ITEMS_PER_PASTE,
    SERIAL_CONTROL,
    SOURCE_CONTROL,
    allowedEntityTypes,
    customerEntityTypeForDestination,
    entityTypeLabel,
    entityTypeLabels,
    serialFieldControl,
    singleEntryColumns,
    singleRowToText,
    entityNamesByID,
    fromPickerDateTime,
    movementPasteSpec,
    movementTypeGuidance,
    sourceControlMode,
    sourceReadoutState,
    toPickerDateTime,
} from "./workOrderHelpers";
import { STATUS_BODY_INK, STATUS_PALETTE, STATUS_SURFACE } from "../statusPalette";

// THE movement. Singular, and there is no way to add a second.
//
// A work order carries ONE movement and as many serials as can be pasted into
// it. The "Movement 1" heading, the numbering and the add/remove scaffolding
// are gone -- a numbered card is a promise of a second card, and nothing in
// the flow ever made one. The PAYLOAD is unchanged: buildWorkOrderPayload
// still sends `movements: [oneMovement]`, so nothing downstream moved.
//
// Reads top to bottom as the sentence the work actually is: what kind of move,
// between whom, and which assets. Remarks and the actions are the form's, not
// the movement's, so they live in WorkOrderForm after this.
//
// Everything a movement is ALLOWED to do comes from its movementType config
// row -- sourceEntityTypes and destinationEntityTypes are JSON lists of
// networkEntityMeta.entityTypeCode, and the create service enforces the very
// same lists, so a picker narrowed by them cannot offer a choice the write
// will refuse.
//
// WHAT IS NOT ON THIS FORM ANY MORE: Return Reason Code, Loan Reference ID and
// Completes Movement Record ID. They are per-ITEM facts wearing a per-movement
// control -- a loan ID belongs to one battery and one customer, not to the
// fifty on the shipment -- and they return as COLUMNS in the serials box, which
// is where the thing that differs per serial belongs. buildMovementPayload
// still reads all three off the movement and still fans them out, so that
// stage is additive and nothing here has to change for it.

// Product controls, not browser controls. The serials box is the one field
// that is deliberately monospaced: it holds a column of identifiers that are
// compared by eye against a report keyed by the same strings, and a
// proportional font makes two serials differing in one digit look identical.
const SERIAL_BOX_SX = {
    "& .MuiOutlinedInput-root textarea": {
        fontFamily: "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "13px",
        lineHeight: "20px",
    },
};

// How many bad lines are named before the list stops. A paste where every
// line is malformed -- the commonest being a three column intake pasted as
// bare serials -- must not render fifty identical messages into the band.
const PASTE_ERRORS_SHOWN = 8;

// Below this, a suggestion request would match most of the registry and answer
// nothing useful.
// One example per column for the single fields, taken from the same sample the
// paste hint shows so the two cannot describe different things.
const SINGLE_PLACEHOLDER = {
    serialNumber: "BAT-51105-B-130455",
    itemName: "48V 105Ah Battery Pack",
    sku: "STD-51V-105AH",
    loanReferenceID: "LN-88120",
};

const MIN_SUGGEST_LENGTH = 2;
const SUGGEST_DEBOUNCE_MS = 300;

// THE PRODUCT'S BUTTON METRICS, with this form's emphasis colour.
//
// 14px/20px, weight 600, radius 10, 8px by 24px of padding -- the same numbers
// BaseFormActionButton sets for every other button in the app, so this one is
// the same SIZE and the same TYPE as the buttons on every page around it. It
// was 13px/500 at radius 8, which is a smaller button in a smaller face, and
// next to a real one it reads as a different class of control.
//
const entityLabel = (entity) =>
    [entity?.name, entity?.entityCode].filter(Boolean).join(" · ") ||
    String(entity?.id ?? "");

// One entity type's worth of options. Narrowed to a SINGLE type rather than
// merged across all of them: the destination is chained -- pick the kind of
// partner, then the partner -- so by the time this renders the type is already
// decided, and a flat list would offer the ones it excludes.
// The sentinel id of the "+ New ..." row.
//
// An OPTION rather than a button beside the field, because that is where the
// user already is when they discover the customer is not in the list -- they
// are looking at the list. A button outside it is a second place to look after
// the first one has already failed them.
//
// A Symbol would be safer against collision but cannot survive MUI's option
// equality, which compares ids as strings. This one cannot collide with a
// networkEntityMeta id, which is an integer.
const CREATE_OPTION_ID = "__create__";

const EntityPicker = ({
    entityTypeCode,
    entityState,
    value,
    onChange,
    error,
    testId,
    disabled,
    placeholder,
    // The entity type this picker may CREATE into, or null. Null is the
    // ordinary case: most destinations are partners an admin sets up on the
    // Network tab, not people a maker meets for the first time mid-delivery.
    createEntityType = null,
    onCreate,
    // The org's word for this entity type, for the notes this control writes.
    typeLabel,
}) => {
    const readableType = typeLabel || entityTypeCode;
    const bucket = entityState?.[entityTypeCode] || null;

    const options = useMemo(() => {
        const existing = (bucket?.records || []).map((entity) => ({
            id: entity.id,
            label: entityLabel(entity),
        }));
        if (!createEntityType) return existing;
        // LAST, not first. The common case is picking someone who already
        // exists, and an action sitting above the data pushes the data down
        // every time the list is opened.
        return [
            ...existing,
            {
                id: CREATE_OPTION_ID,
                label: `+ New ${createEntityType.label || "Customer"}`,
                isCreate: true,
            },
        ];
    }, [bucket, createEntityType]);

    const isLoading = Boolean(bucket?.isLoading);
    const loadError = bucket?.errorMessage || "";
    // The create row is never a SELECTED value -- it is an action wearing an
    // option, and leaving it selectable would let the form submit a
    // destination of "__create__".
    const selected =
        options.find(
            (o) => !o.isCreate && String(o.id) === String(value)
        ) || null;

    return (
        <Box sx={{ minWidth: 0 }}>
            <Autocomplete
                fullWidth
                size="small"
                disabled={disabled}
                options={options}
                value={selected}
                loading={isLoading}
                // NO CLEAR BUTTON. It rendered as an X in a grey hover circle
                // crowding the caret, and it is an action these fields do not
                // have: destination and vendor are required, and the way to
                // change one is to pick another.
                //
                // `value` stays nullable regardless -- disableClearable only
                // removes the control, and the field is genuinely empty until
                // something is picked.
                disableClearable
                forcePopupIcon
                getOptionLabel={(option) => option?.label ?? ""}
                isOptionEqualToValue={(option, val) => String(option.id) === String(val?.id)}
                onChange={(_, option) => {
                    if (option?.isCreate) {
                        onCreate?.();
                        return;
                    }
                    onChange(option ? option.id : null);
                }}
                sx={WORK_ORDER_AUTOCOMPLETE_SX}
                slotProps={WORK_ORDER_AUTOCOMPLETE_SLOT_PROPS}
                // The SAME option row the Select menu draws. A picker and a
                // dropdown sitting in one panel have to have one row, and two
                // copies of `py: 1, pl: 1.5` is how they stop having one.
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                        <Box
                            component="li"
                            key={key}
                            {...rest}
                            data-testid={
                                option.isCreate ? `${testId}-create-option` : undefined
                            }
                            sx={{
                                ...SELECT_ITEM_SX,
                                ...(option.isCreate
                                    ? {
                                          borderTop: `1px solid ${FORM_TOKENS.fieldBorder}`,
                                          color: FORM_TOKENS.purpleDark,
                                          fontWeight: 500,
                                      }
                                    : {}),
                            }}
                        >
                            {option.isCreate ? (
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        color: FORM_TOKENS.purpleDark,
                                    }}
                                >
                                    {option.label}
                                </Typography>
                            ) : (
                                <SelectItemText>{option.label}</SelectItemText>
                            )}
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={isLoading ? "Loading…" : placeholder}
                        error={Boolean(error)}
                        inputProps={{ ...params.inputProps, "data-testid": testId }}
                        sx={WORK_ORDER_AUTOCOMPLETE_INPUT_SX}
                    />
                )}
            />
            <Helper
                text={
                    error ||
                    loadError ||
                    // Counted over the REAL options: with a create row
                    // present the list is never empty, and "none configured"
                    // beside an offer to add one is a contradiction.
                    (!isLoading && !(bucket?.records || []).length
                        ? createEntityType
                            ? `No ${readableType} records yet — add the first one above`
                            : `No ${readableType} entities are configured yet`
                        : "")
                }
                isError={Boolean(error || loadError)}
            />
        </Box>
    );
};

// The From half when Source is DETECTED. A dashed box rather than a field,
// because it is not one: nothing here is typed, and drawing it with a solid
// input border would invite a click that does nothing.
//
// Every word in it comes from sourceReadoutState, which mirrors the TO half's
// states one for one -- see the note there. Nothing is composed here.
const SourceReadout = ({ source, testId }) => {
    // The dot marks a RESOLVED holder. "Choose a movement type" and "taken
    // from the serials once you check them" are prompts, not answers, and a
    // status dot beside them would read as though something had been found.
    const known = source.status === "single" || source.status === "mixed";
    return (
        <Box
            data-testid={testId}
            sx={{
                border: `1px dashed ${FORM_TOKENS.dashedBorder}`,
                borderRadius: "8px",
                backgroundColor: FORM_TOKENS.surface,
                boxSizing: "border-box",
                // The SAME height as the pickers opposite it. FROM is a
                // read-out and TO is a picker, but they are the two ends of
                // one fact and a panel whose halves are different heights
                // reads as two panels.
                minHeight: FIELD_METRICS.height,
                px: FIELD_METRICS.paddingX,
                py: 0,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
            }}
        >
            {known && (
                <Box
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        flexShrink: 0,
                        backgroundColor:
                            source.status === "mixed" ? STATUS_PALETTE.warning.fg : STATUS_PALETTE.success.fg,
                    }}
                />
            )}
            <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.inkSoft }}>
                {source.label ? (
                    <Typography
                        component="span"
                        sx={{ fontSize: "14px", fontWeight: 500, color: FORM_TOKENS.ink }}
                    >
                        {source.label}
                    </Typography>
                ) : null}
                {source.label && source.detail ? " — " : ""}
                {source.detail}
            </Typography>
        </Box>
    );
};

// What to paste, shown ABOVE the box and not in the placeholder.
//
// A placeholder vanishes the moment the box is clicked, which is exactly when
// someone is about to paste -- so the one piece of guidance they need is gone
// at the one moment they need it. This stays on screen.
//
// It shows the column ORDER as chips and ONE sample data row. The sample is
// deliberately drawn as a sample -- dimmed, italic-free monospace, under the
// word "Example" -- because a realistic-looking row above an empty box is a
// row people copy wholesale, and then wonder why the work order contains a
// battery they do not own.
//
// The "no header row" instruction is STATED, not implied by the example. The
// parser skips a header anyway (see isHeaderRow), but telling the user after
// the fact is not the same as telling them first.
const PasteFormatHint = ({ columns, sample, delimiterNote, testId }) => (
    <Box
        data-testid={testId}
        sx={{
            border: `1px solid ${FORM_TOKENS.fieldBorder}`,
            borderRadius: "8px",
            backgroundColor: FORM_TOKENS.sunk,
            px: 2,
            py: 1.5,
            mb: 1.5,
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
                mb: 1,
            }}
        >
            {columns.map((column, index) => (
                <Box key={column.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {index > 0 && (
                        <Typography sx={{ fontSize: "13px", color: FORM_TOKENS.inkDecorative }}>&rsaquo;</Typography>
                    )}
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: "6px",
                            border: `1px solid ${FORM_TOKENS.purpleLight}`,
                            backgroundColor: FORM_TOKENS.purpleLight,
                            color: FORM_TOKENS.purpleDark,
                            fontSize: "12px",
                            fontWeight: 500,
                        }}
                    >
                        {column.label}
                        <Typography
                            component="span"
                            sx={{ fontSize: "11px", color: FORM_TOKENS.inkFaint }}
                        >
                            required
                        </Typography>
                    </Box>
                </Box>
            ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
            <Typography
                sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: FORM_TOKENS.inkFaint,
                    flexShrink: 0,
                }}
            >
                Example
            </Typography>
            <Typography
                data-testid={`${testId}-sample`}
                sx={{
                    fontFamily:
                        "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "12px",
                    color: FORM_TOKENS.inkDecorative,
                    overflowWrap: "anywhere",
                }}
            >
                {sample.join("    ")}
            </Typography>
        </Box>

        <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkSoft, mt: 1 }}>
            Paste the DATA ROWS only — do not include the header row. {delimiterNote}
        </Typography>
    </Box>
);

// The serial field in SINGLE mode, for a movement over assets that already
// exist.
//
// Search and select, against the very endpoint the Assets table's own search
// uses -- so the suggestions are the registry, not a second index that could
// disagree with it.
//
// freeSolo, DELIBERATELY. The suggestion endpoint returns a capped list, so a
// serial that exists but did not surface in ten results must still be
// enterable; trapping the maker inside the suggestions would make a real asset
// unreachable. Nothing is lost by allowing it: Preview and Submit both
// re-resolve every serial server-side, which is where a serial that does not
// exist is actually caught.
//
// It KEEPS its clear button while the entity pickers below drop theirs, and
// that asymmetry is the rule rather than an oversight: freeSolo text fields
// keep clear, entity pickers do not. See WORK_ORDER_CLEARABLE_AUTOCOMPLETE_SX
// for why, and for the wider gutter two end-adornment buttons need.
const AssetSerialPicker = ({ value, onChange, disabled, error, testId }) => {
    const [options, setOptions] = useState([]);
    const [term, setTerm] = useState("");

    useEffect(() => {
        const query = String(term || "").trim();
        if (query.length < MIN_SUGGEST_LENGTH) {
            setOptions([]);
            return undefined;
        }
        // Debounced, unlike the serial CHECK which is an explicit button: this
        // is a typeahead over an index, not a resolution of every serial in a
        // paste, and it is the one place where firing per keystroke pause is
        // the right trade.
        let cancelled = false;
        const timer = setTimeout(async () => {
            const res = await getAssetSearchSuggestions("serialNumber", query);
            if (cancelled) return;
            const values = res?.response?.data;
            setOptions(Array.isArray(values) ? values.filter(Boolean) : []);
        }, SUGGEST_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [term]);

    return (
        <Autocomplete
            freeSolo
            fullWidth
            size="small"
            disabled={disabled}
            options={options}
            // Controlled by the SERIAL STRING, which is what the form holds and
            // what the payload sends. An option object here would need
            // unwrapping in two more places.
            value={value || ""}
            inputValue={value || ""}
            onInputChange={(_, next) => {
                setTerm(next);
                onChange(next);
            }}
            onChange={(_, next) => onChange(typeof next === "string" ? next : "")}
            getOptionLabel={(option) => (typeof option === "string" ? option : "")}
            filterOptions={(all) => all}
            sx={WORK_ORDER_CLEARABLE_AUTOCOMPLETE_SX}
            slotProps={WORK_ORDER_AUTOCOMPLETE_SLOT_PROPS}
            renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                    <Box component="li" key={key} {...rest} sx={SELECT_ITEM_SX}>
                        <SelectItemText>{option}</SelectItemText>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Search a serial number"
                    error={Boolean(error)}
                    inputProps={{ ...params.inputProps, "data-testid": testId }}
                    sx={WORK_ORDER_AUTOCOMPLETE_INPUT_SX}
                />
            )}
        />
    );
};

// Sits level with the middle of the FIRST control on either side, so it reads
// as pointing from one to the other rather than floating between two columns.
//
// The offset is the caption block (an 11px line plus its 10px margin) plus
// half a field, less half the 20px glyph -- derived from the same metrics the
// controls use, so it follows if the field height ever changes.
const ARROW_TOP_OFFSET = `calc(27px + (${FIELD_METRICS.height} / 2) - 10px)`;

const Arrow = () => (
    <Box
        aria-hidden="true"
        sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pt: { xs: 0, md: ARROW_TOP_OFFSET },
            color: FORM_TOKENS.inkDecorative,
            transform: { xs: "rotate(90deg)", md: "none" },
        }}
    >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12h15M13 6l6 6-6 6" />
        </svg>
    </Box>
);

const MovementBuilder = ({
    movement,
    movementTypes = [],
    movementTypesByCode = {},
    entityState = {},
    onChange,
    errors = {},
    disabled = false,
    // {min, max} in "YYYY-MM-DDTHH:mm", resolved ONCE by the form from the
    // ORG's today -- see movementDateTimeBounds -- and enforced again in
    // validateWorkOrderForm, because bounding the picker only stops the
    // picker.
    dateBounds = null,
    // Is the movement date the USER's to set? Decided by the form from the
    // menu item that opened the page -- see isMovementDateEditable. False is
    // the common case: a new movement is happening now, and the clock is the
    // only correct answer.
    dateIsEditable = true,
    // --- the serials check ------------------------------------------------
    // The report and the state machine are owned by the form, which is what
    // makes the request. This component renders them and nothing more.
    serialReport = null,
    checkState = null,
    // The parsed box: columns, rows, per-line errors and the cap. Computed by
    // the form, which also builds the payload from the same parse, so the
    // count on screen and the items on the wire cannot disagree.
    paste = null,
    // pageMeta, read for one thing: the customer entity type definition behind
    // the destination picker's "+ New Customer" option.
    metaInfo = null,
    // Folds a just-created partner into the picker's options, so the maker is
    // not sent to the Network tab to find the customer they just added.
    onEntityCreated = () => {},
    pendingSingleSwitch = null,
    onCancelSingleSwitch = () => {},
    onConfirmSingleSwitch = () => {},
    // Present ONLY after a transport failure -- the check is automatic
    // otherwise, so a permanent button for it would be a control with nothing
    // to do.
    // The form writing to ITSELF -- a single allowed destination type resolved
    // on the maker's behalf. Distinct from onChange because the form treats an
    // onChange as a user touch. Defaults to onChange so a caller that does not
    // care behaves as before.
    onAutoFill = null,
    // The payload is still an array of one, so validateWorkOrderForm still
    // keys its errors by index. Nothing renders it -- there is no movement
    // number on screen any more.
    index = 0,
}) => {
    const movementType = movementTypesByCode[movement?.movementTypeCode] || null;

    const destinationTypes = useMemo(
        () => allowedEntityTypes(movementType, "destination"),
        [movementType]
    );
    const sourceTypes = useMemo(
        () => allowedEntityTypes(movementType, "source"),
        [movementType]
    );
    const sourceMode = useMemo(() => sourceControlMode(movementType), [movementType]);

    const errorAt = useCallback(
        (field) => errors[`movements.${index}.${field}`] || "",
        [errors, index]
    );

    const patch = useCallback(
        (changes) => onChange({ ...movement, ...changes }),
        [movement, onChange]
    );

    // The chained destination: the kind of partner, then the partner. Held on
    // the movement so the two stay in step, and ignored by the payload builder,
    // which reads only destinationEntityID.
    const destinationTypeCode = movement?.destinationEntityTypeCode || "";
    const autoFill = onAutoFill || onChange;

    // One allowed type is not a question. Auto-selected rather than rendered
    // as a dropdown with a single option -- the same reason the Work Order
    // Type control is gone from this page entirely.
    useEffect(() => {
        if (destinationTypes.length !== 1) return;
        if (destinationTypeCode === destinationTypes[0]) return;
        // onAutoFill, NOT onChange. This is the form filling itself in, and
        // the form counts an onChange as "the maker touched it" -- which on an
        // edit is what releases the automatic check. A saved movement whose
        // destination entity has since been soft-deleted opens with no type
        // code, trips this effect, and would mark the form touched and fire a
        // dry run nobody asked for.
        autoFill({
            ...movement,
            destinationEntityTypeCode: destinationTypes[0],
            destinationEntityID: null,
        });
        // `movement` is deliberately absent: this must run when the ALLOWED
        // TYPES change, not on every keystroke elsewhere on the form, and
        // re-running it with a fresh movement object would clear a destination
        // the user had just chosen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationTypes, destinationTypeCode]); // eslint-disable-line

    // The definition behind "+ New Customer", or null -- which HIDES the
    // option. Null covers three real cases: this destination is not a
    // customer, pageMeta has not loaded, and the org never defined a customer
    // type. Offering the option in any of them opens a dialog with no fields.
    const createEntityType = useMemo(
        () => customerEntityTypeForDestination(destinationTypeCode, metaInfo),
        [destinationTypeCode, metaInfo]
    );

    const [createOpen, setCreateOpen] = useState(false);

    // Moves focus to the second half of the chained type -> entity pair.
    //
    // Queried by the same data-blocked-field hook the refusal scroll uses, so
    // there is ONE way this form addresses a field rather than two.
    const focusDestinationEntity = useCallback(() => {
        const field = document.querySelector(
            '[data-blocked-field="destinationEntityID"]'
        );
        field?.querySelector("input")?.focus();
    }, []);

    const handleEntityCreated = useCallback(
        (created) => {
            setCreateOpen(false);
            if (!created?.id) return;
            // Added to the list AND selected. Creating a customer from inside
            // the destination picker is one action, and leaving the field
            // empty afterwards would make it two.
            onEntityCreated(destinationTypeCode, created);
            patch({ destinationEntityID: created.id });
        },
        [destinationTypeCode, onEntityCreated, patch]
    );

    const entityNames = useMemo(() => entityNamesByID(entityState), [entityState]);
    const routeSource = useMemo(
        () => sourceReadoutState({ movementType, report: serialReport, entityNames }),
        [movementType, serialReport, entityNames]
    );

    const serialText = movement?.serialText || "";
    const check = checkState || {};

    // The paste's shape comes from the movement type -- see movementPasteSpec.
    // A Forward moves assets that exist, so a serial is the whole row; a
    // Vendor Intake creates them, so the row has to say what they are.
    const pasteColumns = paste?.columns || movementPasteSpec(movementType).columns;
    const pasteSample = movementPasteSpec(movementType).sample;

    // SINGLE IS THE DEFAULT, so an absent mode reads as single rather than
    // falling through to the box.
    const mode = movement?.entryMode === ENTRY_MODE.PASTE
        ? ENTRY_MODE.PASTE
        : ENTRY_MODE.SINGLE;
    const isSingle = mode === ENTRY_MODE.SINGLE;

    const singleValues = useMemo(() => movement?.singleValues || {}, [movement]);
    const singleColumns = useMemo(
        () => singleEntryColumns(movementType),
        [movementType]
    );
    const serialControl = useMemo(
        () => serialFieldControl(movementType),
        [movementType]
    );

    // ONE FIELD CHANGED -> the field state AND the box, in the same update.
    //
    // This is what makes both modes one payload path. Single mode never builds
    // an item of its own: it writes the row into serialText, and everything
    // after that -- the parser, the validator, the preview, the payload
    // builder -- is the code the paste path already used. There is no second
    // builder to keep in step because there is no second builder.
    const patchSingle = useCallback(
        (key, value) => {
            const next = { ...singleValues, [key]: value };
            patch({
                singleValues: next,
                serialText: singleRowToText(next, singleColumns),
            });
        },
        [patch, singleValues, singleColumns]
    );

    const overCap = Boolean(paste?.capExceeded);
    const maxRows = paste?.maxRows ?? MAX_ITEMS_PER_PASTE;

    // A LIVE count, so the limit is watched rather than hit. The stale note
    // rides on the same line because both describe the same box.
    const countLine = [
        `${paste?.count ?? 0} of ${maxRows}`,
        paste?.headerSkipped ? "header row ignored" : "",
        check.note,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <>
            {/* 1 -- what kind of move. The DATE is not asked for on a create:
                a work order records a movement happening now, so the date is
                the clock's answer and not a question. What will be recorded is
                stated beside Submit, in the preview panel, rather than shown
                twice. The picker returns on an EDIT, where the date of an
                already-raised movement is genuinely a field. */}
            <FormBand testId="work-order-movement">
                <Box
                    sx={{
                        display: "grid",
                        // One column when the date is not asked for, or the
                        // movement type sits in half a card beside a gap.
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: dateIsEditable ? "1fr 1fr" : "1fr",
                        },
                        columnGap: 2.75,
                        rowGap: 2.25,
                    }}
                >
                    <Box sx={{ minWidth: 0 }} data-blocked-field="movementTypeCode">
                        <FieldLabel label="Movement Type" required />
                        {/* The order is the SERVER's -- the config endpoint
                            returns movement types in seed order, which is the
                            order the business thinks in. Nothing here sorts
                            them. */}
                        <WorkOrderSelectField
                            testId="work-order-movement-type"
                            placeholder="Select a movement type"
                            disabled={disabled}
                            error={Boolean(errorAt("movementTypeCode"))}
                            value={movement?.movementTypeCode || ""}
                            options={movementTypes.map((type) => ({
                                value: type.code,
                                label: type.label || type.code,
                            }))}
                            onChange={(e) =>
                                // Clearing both ends in the SAME update as the
                                // type: the previous type's allowed entity
                                // lists no longer apply, and an id left behind
                                // would be submitted against rules that never
                                // permitted it.
                                patch({
                                    movementTypeCode: e.target.value,
                                    sourceEntityID: null,
                                    destinationEntityTypeCode: "",
                                    destinationEntityID: null,
                                })
                            }
                        />
                        <Helper
                            testId="work-order-movement-type-helper"
                            text={
                                errorAt("movementTypeCode") ||
                                movementTypeGuidance(movementType) ||
                                "The movement decides who can send, who can receive, and what state the assets land in."
                            }
                            isError={Boolean(errorAt("movementTypeCode"))}
                        />
                    </Box>

                    {dateIsEditable && (
                    <Box sx={{ minWidth: 0 }} data-blocked-field="movementDateTime">
                        <FieldLabel label="Movement Date & Time" required />
                        {/* The product's own control. `datetime-local` renders
                            differently in every browser, matches nothing else
                            in the app, and puts the calendar in the browser's
                            chrome rather than the page's. The VALUE contract is
                            unchanged -- form state is still
                            "YYYY-MM-DDTHH:mm" and the bounds are still whole
                            days in the org's timezone -- and the one boundary
                            with the picker's own shape is crossed in
                            toPickerDateTime / fromPickerDateTime. */}
                        <Box sx={WORK_ORDER_DATE_FIELD_SX}>
                        <SingleDateTimePicker
                            dataTestId="work-order-movement-datetime"
                            // The form's accent, for the portaled popup that
                            // no wrapper sx can reach.
                            colorPrimary={FORM_TOKENS.purple}
                            value={toPickerDateTime(movement?.movementDateTime)}
                            onChange={(value) =>
                                patch({ movementDateTime: fromPickerDateTime(value) })
                            }
                            placeholder="Select date and time"
                            hasError={Boolean(errorAt("movementDateTime"))}
                            disabled={disabled}
                            minDate={dateBounds?.min || null}
                            maxDate={dateBounds?.max || null}
                            height={FIELD_METRICS.height}
                        />
                        </Box>
                        {/* A date alone is not enough: this becomes
                            assetLifecycleRecord.effectiveFrom on approval,
                            which is what orders the asset's entire history.
                            Two movements on the same day have to be
                            separable. */}
                        <Helper
                            text={
                                errorAt("movementDateTime") ||
                                (dateBounds?.min && dateBounds?.max
                                    ? `Your timezone, stored in UTC. Anywhere from ${dateBounds.min.slice(0, 10)} to ${dateBounds.max.slice(0, 10)}.`
                                    : "Your timezone, stored in UTC.")
                            }
                            isError={Boolean(errorAt("movementDateTime"))}
                        />
                    </Box>
                    )}
                </Box>
            </FormBand>

            {/* 2 -- WHICH ASSETS, and it comes BEFORE the Route panel.
                The assets are what the order is about, and the Route panel
                below them is READ FROM these serials -- so asking for them
                after showing a panel derived from them had the page
                explaining an answer before anyone had asked the question. */}
            <FormBand testId="work-order-serials-band">
                {/* NO MODE TOGGLE. The question -- one asset or several --
                    is answered before the page, by the floating button that
                    opened it, and a page that then asks again is asking
                    someone to repeat themselves. The band header carries the
                    cap instead, which is the one thing a paste needs to know
                    up front. */}
                <BandHead
                    title="Assets in this work order"
                    note={
                        isSingle
                            ? ""
                            : `Up to ${paste?.maxRows ?? MAX_ITEMS_PER_PASTE} at a time`
                    }
                />

                {isSingle ? (
                    <Box
                        data-testid="work-order-single-entry"
                        data-blocked-field="serials"
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: singleColumns.length > 1 ? "1fr 1fr" : "1fr",
                            },
                            columnGap: 2.75,
                            rowGap: 2.25,
                        }}
                    >
                        {/* DERIVED FROM THE SPEC, never a hand-written layout
                            per movement type. A column added to
                            movementPasteSpec grows a field here for free, and
                            three hand-written layouts would not. */}
                        {singleColumns.map((column) => {
                            const isSerial = column.key === "serialNumber";
                            // Keyed per FIELD, set by the form when it blocks.
                            // Not derived from singleErrors directly: a form
                            // that reddens every empty box the moment it opens
                            // is shouting before the user has done anything.
                            const fieldError =
                                errorAt(`single.${column.key}`) ||
                                (isSerial ? errorAt("serials") : "");
                            return (
                                <Box
                                    key={column.key}
                                    sx={{ minWidth: 0 }}
                                    data-blocked-field={column.key}
                                >
                                    <FieldLabel label={column.label} required />
                                    {isSerial &&
                                    serialControl === SERIAL_CONTROL.SEARCH ? (
                                        <AssetSerialPicker
                                            value={singleValues.serialNumber || ""}
                                            onChange={(next) =>
                                                patchSingle("serialNumber", next)
                                            }
                                            disabled={disabled}
                                            error={Boolean(fieldError)}
                                            testId="work-order-single-serialNumber"
                                        />
                                    ) : (
                                        <StyledTextField
                                            fullWidth
                                            size="small"
                                            disabled={disabled}
                                            value={singleValues[column.key] || ""}
                                            error={Boolean(fieldError)}
                                            placeholder={
                                                SINGLE_PLACEHOLDER[column.key] || ""
                                            }
                                            onChange={(e) =>
                                                patchSingle(column.key, e.target.value)
                                            }
                                            inputProps={{
                                                "data-testid": `work-order-single-${column.key}`,
                                            }}
                                        />
                                    )}
                                    <Helper
                                        text={
                                            fieldError ||
                                            (isSerial &&
                                            serialControl === SERIAL_CONTROL.TEXT
                                                ? "This movement registers the asset, so the serial will not be in the registry yet."
                                                : "")
                                        }
                                        isError={Boolean(fieldError)}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                <>
                <PasteFormatHint
                    testId="work-order-paste-format"
                    columns={pasteColumns}
                    sample={pasteSample}
                    delimiterNote={
                        pasteColumns.length > 1
                            ? "Tab or comma separated — a block copied straight out of a spreadsheet works as it is."
                            : "One serial per line, or comma separated."
                    }
                />

                {/* A TEXT BOX AND NOT CHIPS. A chip destroys the line it came
                    from, and the line is the row: a Vendor Intake row is a
                    serial plus its item name and SKU, so a block copied out of
                    a spreadsheet has to survive the paste intact to be parsed
                    as one. */}
                <StyledTextField
                    fullWidth
                    data-blocked-field="serials"
                    multiline
                    minRows={3}
                    maxRows={14}
                    size="small"
                    disabled={disabled}
                    value={serialText}
                    error={Boolean(errorAt("serials"))}
                    placeholder={pasteSample.join(
                        pasteColumns.length > 1 ? "\t" : "\n"
                    )}
                    onChange={(e) => patch({ serialText: e.target.value })}
                    inputProps={{ "data-testid": "work-order-serials-input" }}
                    sx={SERIAL_BOX_SX}
                />
                </>
                )}

                {/* THE COUNT AND THE CAP ARE A PASTE CONCERN. Single mode
                    is one asset by construction, so "1 of 50" there would be
                    answering a question nobody asked. */}
                {!isSingle && (
                    <Box sx={{ minWidth: 0, mt: 1.25 }}>
                        <Helper
                            testId="work-order-serials-helper"
                            text={errorAt("serials") || countLine}
                            isError={Boolean(errorAt("serials")) || overCap}
                        />
                    </Box>
                )}

                {/* PER LINE, and never a count on its own. "3 rows could not
                    be read" in a fifty row paste is not something anyone can
                    act on; the line number is the whole of the fix. */}
                {!isSingle && paste?.errors?.length > 0 && (
                    <Box
                        data-testid="work-order-paste-errors"
                        sx={{
                            mt: 1.5,
                            border: `1px solid ${STATUS_PALETTE.error.border}`,
                            backgroundColor: STATUS_SURFACE.error,
                            borderRadius: "8px",
                            px: 2,
                            py: 1.25,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: FORM_TOKENS.danger,
                                mb: 0.5,
                            }}
                        >
                            {paste.errors.length}{" "}
                            {paste.errors.length === 1 ? "row" : "rows"} could not be read
                        </Typography>
                        {paste.errors.slice(0, PASTE_ERRORS_SHOWN).map((error) => (
                            <Typography
                                key={`${error.line}-${error.message}`}
                                data-testid={`work-order-paste-error-${error.line}`}
                                sx={{
                                    fontSize: "12px",
                                    color: STATUS_BODY_INK.error,
                                    lineHeight: "18px",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {error.message}
                            </Typography>
                        ))}
                        {paste.errors.length > PASTE_ERRORS_SHOWN && (
                            <Typography sx={{ fontSize: "12px", color: STATUS_BODY_INK.error, mt: 0.5 }}>
                                …and {paste.errors.length - PASTE_ERRORS_SHOWN} more.
                            </Typography>
                        )}
                    </Box>
                )}

            </FormBand>

            {/* 3 -- between whom. ONE panel, not two loose fields: source and
                destination are one fact, and the arrow between them is the
                only thing that says which way the assets go. */}
            <FormBand testId="work-order-route-band">
                <BandHead
                    // "Route", not "Custody". A FROM -> TO pair IS a route,
                    // it is a word anyone reads without training, and it works
                    // for all five movement types. "Custody" is the word the
                    // data model uses; it is not the word a maker thinks in.
                    title="Route"
                    // Withheld until a movement type is chosen, for the same
                    // reason the FROM helper is: before then there is no
                    // source behaviour to describe, and promising one is the
                    // misleading half of what this panel used to say.
                    note={
                        movementType && sourceMode === SOURCE_CONTROL.READOUT
                            ? "Source is read from the serials you enter below"
                            : ""
                    }
                />
                <Box
                    sx={{
                        border: `1px solid ${FORM_TOKENS.fieldBorder}`,
                        borderRadius: "8px",
                        backgroundColor: FORM_TOKENS.sunk,
                        px: 2.25,
                        py: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 34px 1fr" },
                            alignItems: "start",
                            rowGap: 1.75,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <RouteCaption>
                                From
                                {sourceTypes.length
                                    ? ` · ${entityTypeLabels(sourceTypes, metaInfo).join(", ")}`
                                    : ""}
                            </RouteCaption>

                            {sourceMode === SOURCE_CONTROL.PICKER ? (
                                <>
                                    {/* The ONE case where the source is a real
                                        choice: this movement type registers
                                        what it moves, so the assets do not
                                        exist yet, there is nothing to read
                                        a holder back from, and the vendor
                                        genuinely is a decision. */}
                                    <EntityPicker
                                        entityTypeCode={sourceTypes[0]}
                                        entityState={entityState}
                                        value={movement?.sourceEntityID ?? null}
                                        onChange={(id) => patch({ sourceEntityID: id })}
                                        error={errorAt("sourceEntityID")}
                                        testId="work-order-movement-source"
                                        disabled={disabled}
                                        typeLabel={entityTypeLabel(sourceTypes[0], metaInfo)}
                                        placeholder={`Search or select ${entityTypeLabel(sourceTypes[0], metaInfo)}`}
                                    />
                                    <Helper text="These serials are new to the registry, so where they came from is yours to state." />
                                </>
                            ) : sourceMode === SOURCE_CONTROL.READOUT ? (
                                <>
                                    <SourceReadout
                                        source={routeSource}
                                        testId="work-order-movement-source-readout"
                                    />
                                    {/* Withheld until a movement type is
                                        chosen: explaining a read-out that is
                                        not showing anything yet is noise. */}
                                    <Helper text={routeSource.helper} />
                                </>
                            ) : (
                                <Helper text="This movement type takes no source." />
                            )}
                        </Box>

                        <Arrow />

                        <Box sx={{ minWidth: 0 }} data-blocked-field="destinationEntityID">
                            <RouteCaption>
                                To
                                {destinationTypeCode
                                    ? ` · ${entityTypeLabel(destinationTypeCode, metaInfo)}`
                                    : ""}
                            </RouteCaption>

                            {destinationTypes.length === 0 ? (
                                <Helper
                                    text={
                                        movementType
                                            ? "This movement type takes no destination."
                                            : "Choose a movement type to see where these assets can go."
                                    }
                                />
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                    {/* Chained. One allowed type is not a
                                        question, so it is picked for the user
                                        rather than offered as a dropdown with
                                        a single option. */}
                                    {destinationTypes.length > 1 && (
                                        <WorkOrderSelectField
                                            testId="work-order-movement-destination-type"
                                            placeholder="Select a partner type"
                                            disabled={disabled}
                                            value={destinationTypeCode}
                                            // The ORG's label, not the code.
                                            // "subDistributor" is what the
                                            // config lists and the API takes;
                                            // it is not a word anybody says.
                                            options={destinationTypes.map((code) => ({
                                                value: code,
                                                label: entityTypeLabel(code, metaInfo),
                                            }))}
                                            onChange={(e) => {
                                                patch({
                                                    destinationEntityTypeCode: e.target.value,
                                                    // The entity belonged to
                                                    // the old type's list.
                                                    destinationEntityID: null,
                                                });
                                                // CHOOSING THE TYPE IS HALF A
                                                // DECISION. Leaving focus
                                                // behind let people scroll on
                                                // and discover at validation
                                                // that the entity was never
                                                // picked, so focus follows to
                                                // the second half.
                                                //
                                                // After paint: the picker is
                                                // re-rendered for the new type
                                                // and the input that exists
                                                // now is not the one that will
                                                // exist then.
                                                requestAnimationFrame(() =>
                                                    focusDestinationEntity()
                                                );
                                            }}
                                        />
                                    )}

                                    {destinationTypeCode ? (
                                        <EntityPicker
                                            entityTypeCode={destinationTypeCode}
                                            entityState={entityState}
                                            value={movement?.destinationEntityID ?? null}
                                            onChange={(id) => patch({ destinationEntityID: id })}
                                            error={errorAt("destinationEntityID")}
                                            testId="work-order-movement-destination"
                                            disabled={disabled}
                                            typeLabel={entityTypeLabel(destinationTypeCode, metaInfo)}
                                            placeholder={`Search or select ${entityTypeLabel(destinationTypeCode, metaInfo)}`}
                                            createEntityType={createEntityType}
                                            onCreate={() => setCreateOpen(true)}
                                        />
                                    ) : (
                                        <Helper
                                            text={errorAt("destinationEntityID")}
                                            isError={Boolean(errorAt("destinationEntityID"))}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </FormBand>

            {/* THE NETWORK TAB'S OWN DIALOG, not a copy of it. Same component,
                same config-driven fields, same createNetworkEntity call -- so a
                customer added here is identical to one added there, and a
                field added to the entity definition reaches both without
                anybody remembering this one exists.

                Mounted only with a definition in hand: it builds its entire
                form from entityType.properties. */}
            {createEntityType && (
                <CreateEditEntityDialog
                    open={createOpen}
                    entityType={createEntityType}
                    // No `entity` -- this is the create path, always.
                    entity={null}
                    onClose={() => setCreateOpen(false)}
                    onSuccess={handleEntityCreated}
                />
            )}
        </>
    );
};

export default MovementBuilder;
